import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SelectDropdown from "../../components-react/SelectDropdown";
import ReactQuillTextEditor from "../../components-react/inputs/ReactQuillTextEditor";
import { fetchAllWorkoutPackageData } from "../../../redux/workout-reducers/workoutApis";
import "../../../styles/centralised-plan/centralised-plans.css";
import { fetchCentralisedPlanCategoriesData } from "../../../redux/plans-reducers/plansSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import CloseSubmitBtn from "../../common-components/custom-comps/CloseSubmitBtn";
import MealsStructure from "../../diet/meal/MealsStructure";
import { stripHtml } from "../../common-data/Conversions";

const CentralisedForm = ({
  userInfoData,
  closeModal,
  isEdit,
  rowData,
  callFunction,
  isDisabled = false,
}) => {
  const dispatch = useDispatch();
  const { loading, workoutPackages } = useSelector((state) => state.workoutApi);
  const { centralisedPlanCategories, centralisedPlanCategoriesLoading } =
    useSelector((state) => state.plans);

  // State to store formatted workout package options
  const [workoutPackageOptions, setWorkoutPackageOptions] = useState([]);
  const [planCategoryOptions, setPlanCategoryOptions] = useState([]);

  // Initial form values
  const iniFormValues = {
    planCategory: "",
    planName: "",
    couplePlanName: "",
    workoutPackage: "",
    duration: "",
    basePrice: "",
    basePriceGst: "",
    finalBasePrice: "",
    sellingPrices: [
      {
        finalSellingPrice: "",
        finalSellingPriceGst: "",
        sellingPrice: "",
        extendableDays: "",
      },
    ],
    coupleSellingPrices: [
      {
        finalSellingPrice: "",
        finalSellingPriceGst: "",
        sellingPrice: "",
        extendableDays: "",
      },
    ],
    description: "",
    isPartialAllowed: false,
    isCouplePlan: false,
    isPlanActive: true,
    isAllGymAccess: false,

    firstInstallmentPercent: "",
    secondInstallmentPercent: "",
    secondInstallmentDueDate: "",
    thirdInstallmentPercent: "",
    thirdInstallmentDueDate: "",
    maxPartialPaymentsPerMonth: "",
  };
  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [showAdditionalSellingPrices, setShowAdditionalSellingPrices] =
    useState(true);
  const [
    showAdditionalCoupleSellingPrices,
    setShowAdditionalCoupleSellingPrices,
  ] = useState(true);
  const formRef = useRef({});

  // Calculate GST and prices
  const calcTax = (action) => {
    try {
      if (isNaN(action?.value)) return 0;
      const tax = parseFloat(((Number(action?.value) * 18) / 100).toFixed(2));
      switch (action?.type) {
        case "tax":
          return tax;
        case "total":
          const total = parseFloat((Number(action?.value) + tax).toFixed(2));
          return total;
        case "tax_inclusive":
          const taxInclusive = parseFloat(
            ((Number(action?.value) / 1.18) * 0.18).toFixed(2)
          );
          return taxInclusive;
        case "pre_gst":
          const pregst = parseFloat((Number(action?.value) / 1.18).toFixed(2));
          return pregst;
        default:
          return;
      }
    } catch (error) {
      console.log("calcTax error:", error);
    }
  };

  // Handle input changes
  const onChangeInput = (e) => {
    if (isDisabled) return;

    const { name, value, type, checked } = e.target;
    setIsSubmit(false);

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "basePrice") {
        newValues.basePriceGst = calcTax({ type: "tax", value });
        newValues.finalBasePrice = calcTax({ type: "total", value });
      }

      if (
        name === "isCouplePlan" &&
        checked &&
        !newValues.coupleSellingPrices
      ) {
        newValues.coupleSellingPrices = [
          {
            finalSellingPrice: "",
            finalSellingPriceGst: "",
            sellingPrice: "",
            extendableDays: "",
          },
        ];
      }

      return newValues;
    });
  };

  // Handle selling price changes - remove formatting
  const onChangeSellingPrice = (index, value) => {
    if (isDisabled) return;

    setFormValues((prev) => {
      const newValues = { ...prev };
      newValues.sellingPrices[index].finalSellingPrice = value;
      newValues.sellingPrices[index].finalSellingPriceGst = calcTax({
        type: "tax_inclusive",
        value,
      });
      newValues.sellingPrices[index].sellingPrice = calcTax({
        type: "pre_gst",
        value,
      });
      return newValues;
    });
  };

  // Handle couple selling price changes - remove formatting
  const onChangeCoupleSellingPrice = (index, value) => {
    if (isDisabled) return;

    setFormValues((prev) => {
      const newValues = { ...prev };
      newValues.coupleSellingPrices[index].finalSellingPrice = value;
      newValues.coupleSellingPrices[index].finalSellingPriceGst = calcTax({
        type: "tax_inclusive",
        value,
      });
      newValues.coupleSellingPrices[index].sellingPrice = calcTax({
        type: "pre_gst",
        value,
      });
      return newValues;
    });
  };

  // Handle extendable days changes
  const onChangeExtendableDays = (index, value) => {
    if (isDisabled) return;

    setFormValues((prev) => {
      const newValues = { ...prev };
      newValues.sellingPrices[index].extendableDays = value;
      return newValues;
    });
  };

  // Handle couple extendable days changes
  const onChangeCoupleExtendableDays = (index, value) => {
    if (isDisabled) return;

    setFormValues((prev) => {
      const newValues = { ...prev };
      newValues.coupleSellingPrices[index].extendableDays = value;
      return newValues;
    });
  };

  // Add new selling price block
  const addSellingPrice = () => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      sellingPrices: [
        ...prev.sellingPrices,
        {
          finalSellingPrice: "",
          finalSellingPriceGst: "",
          sellingPrice: "",
          extendableDays: "",
        },
      ],
    }));
  };

  // Add new couple selling price block
  const addCoupleSellingPrice = () => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      coupleSellingPrices: [
        ...prev.coupleSellingPrices,
        {
          finalSellingPrice: "",
          finalSellingPriceGst: "",
          sellingPrice: "",
          extendableDays: "",
        },
      ],
    }));
  };

  // Remove selling price block
  const removeSellingPrice = (index) => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      sellingPrices: prev.sellingPrices.filter((_, i) => i !== index),
    }));
  };

  // Remove couple selling price block
  const removeCoupleSellingPrice = (index) => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      coupleSellingPrices: prev.coupleSellingPrices.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Handle dropdown changes
  const onChangeDropdown = (value, name) => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsSubmit(false);
  };

  // Form validation
  const validateForm = (values) => {
    let errors = {};

    if (!values.planCategory) {
      errors.planCategory = "Plan category is required!";
    }

    if (!values.planName) {
      errors.planName = "Plan name is required!";
    }

    // if (!values.workoutPackage) {
    //   errors.workoutPackage = "Workout package is required!";
    // }

    if (!values.duration) {
      errors.duration = "Duration is required!";
    } else if (
      isNaN(values.duration) ||
      !Number.isInteger(Number(values.duration)) ||
      Number(values.duration) < 1
    ) {
      errors.duration = "Duration must be a positive integer!";
    }

    if (!values.basePrice) {
      errors.basePrice = "Base price is required!";
    } else if (isNaN(values.basePrice)) {
      errors.basePrice = "Invalid Amount!";
    } else if (Number(values.basePrice) < 0) {
      errors.basePrice = "Base price cannot be negative!";
    }

    // Modified description validation
    const strippedDescription = stripHtml(values.description).trim();
    if (!strippedDescription) {
      errors.description = "Description is required!";
    } else if (strippedDescription.length < 10) {
      errors.description = "Description is too short!";
    } else if (strippedDescription.length > 400) {
      errors.description = "Description should be less than 400 characters!";
    }

    values.sellingPrices.forEach((price, index) => {
      if (!price.finalSellingPrice) {
        errors[`sellingPrice_${index}`] = "Final selling price is required!";
      } else if (isNaN(price.finalSellingPrice)) {
        errors[`sellingPrice_${index}`] = "Invalid Amount!";
      } else if (Number(price.finalSellingPrice) < 0) {
        errors[`sellingPrice_${index}`] =
          "Final selling price cannot be negative!";
      } else if (
        parseFloat(price.finalSellingPrice) <= parseFloat(values.finalBasePrice)
      ) {
        errors[`sellingPrice_${index}`] =
          "It must be greater than Final Base Price!";
      }
    });

    if (values.isCouplePlan) {
      values.coupleSellingPrices.forEach((price, index) => {
        if (!price.finalSellingPrice) {
          errors[`coupleSellingPrice_${index}`] =
            "Couple final selling price is required!";
        } else if (isNaN(price.finalSellingPrice)) {
          errors[`coupleSellingPrice_${index}`] = "Invalid Amount!";
        } else if (Number(price.finalSellingPrice) < 0) {
          errors[`coupleSellingPrice_${index}`] =
            "Final selling price cannot be negative!";
        } else if (
          parseFloat(price.finalSellingPrice) <=
          parseFloat(values.finalBasePrice)
        ) {
          errors[`coupleSellingPrice_${index}`] =
            "It must be greater than Final Base Price!";
        }

        if (price.extendableDays && Number(price.extendableDays) < 0) {
          errors[`coupleExtendableDays_${index}`] =
            "Extendable days cannot be negative!";
        }
      });
    }

    // Add validation for installment fields when partial is allowed
    if (values.isPartialAllowed) {
      if (!values.firstInstallmentPercent) {
        errors.firstInstallmentPercent =
          "First installment percentage is required!";
      } else if (isNaN(values.firstInstallmentPercent)) {
        errors.firstInstallmentPercent =
          "Installment Percentage must be a number!";
      } else if (Number(values.firstInstallmentPercent) < 0) {
        errors.firstInstallmentPercent = "Percentage cannot be negative!";
      }

      if (!values.secondInstallmentPercent) {
        errors.secondInstallmentPercent =
          "Second installment percentage is required!";
      } else if (isNaN(values.secondInstallmentPercent)) {
        errors.secondInstallmentPercent =
          "Installment Percentage must be a number!";
      } else if (Number(values.secondInstallmentPercent) < 0) {
        errors.secondInstallmentPercent = "Percentage cannot be negative!";
      }

      if (!values.secondInstallmentDueDate) {
        errors.secondInstallmentDueDate =
          "Second installment due days is required!";
      }

      // Validate third installment - if one field is entered, require the other
      if (values.thirdInstallmentPercent && !values.thirdInstallmentDueDate) {
        errors.thirdInstallmentDueDate =
          "Third installment due days is required when percentage is provided!";
      }

      if (!values.thirdInstallmentPercent && values.thirdInstallmentDueDate) {
        errors.thirdInstallmentPercent =
          "Third installment percentage is required when due days is provided!";
      }

      if (
        values.thirdInstallmentPercent &&
        isNaN(values.thirdInstallmentPercent)
      ) {
        errors.thirdInstallmentPercent =
          "Installment Percentage must be a number!";
      }

      // Validate that all percentages add up to 100%
      const firstPercent = Number(values.firstInstallmentPercent) || 0;
      const secondPercent = Number(values.secondInstallmentPercent) || 0;
      const thirdPercent = Number(values.thirdInstallmentPercent) || 0;
      const totalPercent = firstPercent + secondPercent + thirdPercent;

      if (totalPercent !== 100) {
        errors.firstInstallmentPercent =
          "All installment percentages must sum to 100%";
        errors.secondInstallmentPercent =
          "All installment percentages must sum to 100%";
        if (values.thirdInstallmentPercent) {
          errors.thirdInstallmentPercent =
            "All installment percentages must sum to 100%";
        }
      }

      if (!values.maxPartialPaymentsPerMonth) {
        errors.maxPartialPaymentsPerMonth =
          "Maximum partial payments per month is required!";
      } else if (isNaN(values.maxPartialPaymentsPerMonth)) {
        errors.maxPartialPaymentsPerMonth = "This must be a number!";
      } else if (Number(values.maxPartialPaymentsPerMonth) < 0) {
        errors.maxPartialPaymentsPerMonth = "Value cannot be negative!";
      }
    }

    if (values.isCouplePlan && !values.couplePlanName) {
      errors.couplePlanName = "Couple plan name is required!";
    }

    return errors;
  };

  // Handle form submission
  const onSubmit = () => {
    const errors = validateForm(formValues);
    setFormErrors(errors);
    setIsSubmit(true);

    if (formRef.current) {
      const firstErrorField = Object.keys(errors).find(
        (field) => formRef.current[field]
      );

      if (firstErrorField) {
        formRef.current[firstErrorField].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  // Assign refs to form fields
  const assignRef = (fieldName) => (el) => {
    if (!formRef.current) {
      formRef.current = {};
    }
    if (el) {
      formRef.current[fieldName] = el;
    }
  };

  function getWorkoutPackages(filters) {
    dispatch(
      fetchAllWorkoutPackageData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  const handleSubmitForm = async (payload) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = isEdit ? "/plan/draft/V2/update" : "/plan/draft/V2/add";
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(iniFormValues);
        callFunction();
        closeModal();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  useEffect(() => {
    const sendableFilters = { page: 1, limit: 1000, status: "active" };

    dispatch(
      fetchCentralisedPlanCategoriesData({
        filters: sendableFilters,
        token: userInfoData?.token,
      })
    );
  }, []);

  useEffect(() => {
    const sendableFilters = { page: 1, limit: 1000, status: "active" };

    getWorkoutPackages(sendableFilters);
  }, []);

  // Format plan categories data when it's loaded
  useEffect(() => {
    if (
      !centralisedPlanCategoriesLoading &&
      centralisedPlanCategories?.status &&
      centralisedPlanCategories?.data
    ) {
      const formattedOptions = centralisedPlanCategories.data.map(
        (category) => ({
          label: category.name || "Unnamed Category", // Fallback for empty names
          value: category.uid,
          info: category, // Keep the full category object for reference
        })
      );
      setPlanCategoryOptions(formattedOptions);
    } else {
      // Reset options if data is not available
      setPlanCategoryOptions([]);
    }
  }, [centralisedPlanCategoriesLoading, centralisedPlanCategories]);

  // Format workout package data when it's loaded
  useEffect(() => {
    if (!loading && workoutPackages?.status && workoutPackages?.data) {
      const formattedOptions = workoutPackages.data.map((pkg) => ({
        label: pkg.plan_name,
        value: pkg.uid,
        info: pkg, // Store the entire package object
      }));
      setWorkoutPackageOptions(formattedOptions);
    }
  }, [loading, workoutPackages]);

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors)?.length === 0) {
      const allPrices = [
        ...formValues.sellingPrices.map((price) => ({
          added_price: parseFloat(
            parseFloat(price.finalSellingPrice).toFixed(2)
          ),
          extend_days: price.extendableDays || 0,
          is_couple: "0",
          offer_value: "",
        })),
        ...(formValues.isCouplePlan
          ? formValues.coupleSellingPrices.map((price) => ({
              added_price: parseFloat(
                parseFloat(price.finalSellingPrice).toFixed(2)
              ),
              extend_days: price.extendableDays || 0,
              is_couple: "1",
              offer_value: "",
            }))
          : []),
      ];

      const originalPrice = parseFloat(formValues.basePrice || 0);
      const priceWithGst = parseFloat((originalPrice * 1.18).toFixed(2));

      const userData = {
        plan_name: formValues.planName,
        couple_plan_name: formValues.isCouplePlan
          ? formValues.couplePlanName
          : "",
        category_id: formValues.planCategory?.value || "",
        package_id: formValues.workoutPackage?.value || "",
        duration: formValues.duration,
        price: priceWithGst,
        original_price: parseFloat(originalPrice.toFixed(2)),
        description: stripHtml(formValues.description)
          .trim()
          .replace(/'/g, "''"),
        status: formValues.isPlanActive ? "active" : "inactive",

        plan_price: {
          data: allPrices,
          type: "regular",
        },

        couple_included: formValues.isCouplePlan ? 1 : 0,
        is_partial: formValues.isPartialAllowed ? 1 : 0,
        first_emi_percentage: formValues.isPartialAllowed
          ? formValues.firstInstallmentPercent
          : 0,
        second_emi_percentage: formValues.isPartialAllowed
          ? formValues.secondInstallmentPercent
          : 0,
        third_emi_percentage: formValues.isPartialAllowed
          ? formValues.thirdInstallmentPercent
          : 0,
        second_emi_duration: formValues.isPartialAllowed
          ? formValues.secondInstallmentDueDate
          : 0,
        third_emi_duration: formValues.isPartialAllowed
          ? formValues.thirdInstallmentDueDate
          : 0,
        fd_link_allowed: formValues.isPartialAllowed
          ? formValues.maxPartialPaymentsPerMonth
          : 0,

        is_allgym_access: formValues.isAllGymAccess ? 1 : 0,
      };

      // Add UID for edit mode
      if (isEdit) {
        userData.uid = rowData?.uid || "";
      }

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure!`,
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors]);

  // Modify the useEffect to populate form when rowData is provided, regardless of isEdit mode
  useEffect(() => {
    // Populate form data when rowData is provided, either in edit mode or disabled mode
    if (rowData) {
      // First, separate regular and couple prices
      const regularPrices =
        rowData.price_details?.filter((price) => price.is_couple === 0) || [];
      const couplePrices =
        rowData.price_details?.filter((price) => price.is_couple === 1) || [];

      // Format the selling prices
      const formattedSellingPrices = regularPrices.map((price) => {
        const finalSellingPrice = price.plan_price;
        const finalSellingPriceGst = calcTax({
          type: "tax_inclusive",
          value: finalSellingPrice,
        });
        const sellingPrice = calcTax({
          type: "pre_gst",
          value: finalSellingPrice,
        });

        return {
          finalSellingPrice,
          finalSellingPriceGst,
          sellingPrice,
          extendableDays: price.extend_days || "",
        };
      });

      // Format the couple selling prices
      const formattedCoupleSellingPrices = couplePrices.map((price) => {
        const finalSellingPrice = price.plan_price;
        const finalSellingPriceGst = calcTax({
          type: "tax_inclusive",
          value: finalSellingPrice,
        });
        const sellingPrice = calcTax({
          type: "pre_gst",
          value: finalSellingPrice,
        });

        return {
          finalSellingPrice,
          finalSellingPriceGst,
          sellingPrice,
          extendableDays: price.extend_days || "",
        };
      });

      // Calculate base price, GST, and total
      const basePrice = rowData.original_price || "";
      const basePriceGst = calcTax({ type: "tax", value: basePrice });
      const finalBasePrice = calcTax({ type: "total", value: basePrice });

      // Format the category as an object with label and value
      const selectedCategory = planCategoryOptions.find(
        (option) => option.value === rowData.category_id
      ) || {
        label: rowData.category_name || "",
        value: rowData.category_id || "",
      };

      // Format the workout package as an object with label and value
      const packageName = rowData.package_name
        ? rowData.package_name.replace(/^"\\"|\\""$/g, "")
        : ""; // This removes the enclosing \"quotes\"

      const selectedWorkoutPackage = {
        label: packageName || "WTF Featured Program", // Use the extracted package name
        value: rowData.package_id || "",
      };

      // Set form values with data from rowData
      setFormValues({
        planCategory: selectedCategory,
        planName: rowData.plan_name || "",
        couplePlanName: rowData.couple_plan_name || "",
        workoutPackage: selectedWorkoutPackage,
        duration: rowData.duration || "",
        basePrice: basePrice,
        basePriceGst: basePriceGst,
        finalBasePrice: finalBasePrice,
        sellingPrices:
          formattedSellingPrices.length > 0
            ? formattedSellingPrices
            : [
                {
                  finalSellingPrice: "",
                  finalSellingPriceGst: "",
                  sellingPrice: "",
                  extendableDays: "",
                },
              ],
        coupleSellingPrices:
          formattedCoupleSellingPrices.length > 0
            ? formattedCoupleSellingPrices
            : [
                {
                  finalSellingPrice: "",
                  finalSellingPriceGst: "",
                  sellingPrice: "",
                  extendableDays: "",
                },
              ],
        description: rowData.description || "",
        isPartialAllowed: rowData.is_partial === 1,
        isCouplePlan: couplePrices.length > 0,
        isPlanActive: rowData.status === "active",
        isAllGymAccess: rowData.is_allgym_access === 1,

        firstInstallmentPercent: rowData.first_emi_percentage || "",
        secondInstallmentPercent: rowData.second_emi_percentage || "",
        secondInstallmentDueDate: rowData.second_emi_duration || "",
        thirdInstallmentPercent: rowData.third_emi_percentage || "",
        thirdInstallmentDueDate: rowData.third_emi_duration || "",
        maxPartialPaymentsPerMonth: rowData.fd_link_allowed || "",
      });
    }
  }, [rowData, planCategoryOptions, workoutPackageOptions]);

  console.log(formValues);

  return (
    <div className="form-container open-form-page-container">
      {!isDisabled && (
        <>
          <MealsStructure text="Centralised Plan Form" />

          {/* <CommonHeadingNBtns
            title="Centralised Plan Form"
            btnTxt={isEdit ? "Update Plan" : "Add Plan"}
            onClose={closeModal}
            onClick={onSubmit}
            isLoading={false}
            isDisabled={false}
          /> */}
        </>
      )}

      <form className="formContainer" style={{ marginTop: "30px" }}>
        <div
          className={`formInputsGrid fourGridSystem scroll-container ${
            isDisabled ? "disabled-form" : ""
          }`}>
          {/* Plan Category Dropdown */}
          <div className="formInputBox">
            <label htmlFor="planCategory" className="formInputLabel">
              Plan Category <span className="required">*</span>
            </label>
            <div
              className={`commonWrapper ${
                formErrors.planCategory ? "formError" : ""
              }`}
              ref={assignRef("planCategory")}>
              <SelectDropdown
                options={planCategoryOptions}
                selectedOption={formValues.planCategory}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "planCategory")
                }
                name="Plan Category"
                isLoading={centralisedPlanCategoriesLoading}
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">{formErrors.planCategory || ""}</span>
          </div>

          {/* Plan Name */}
          <div className="formInputBox">
            <label htmlFor="planName" className="formInputLabel">
              Plan Name <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Plan Name"
              className={`formInput ${formErrors.planName ? "formError" : ""}`}
              name="planName"
              value={formValues.planName}
              onChange={onChangeInput}
              ref={assignRef("planName")}
              disabled={isDisabled}
            />
            <span className="error-label">{formErrors.planName || ""}</span>
          </div>

          {/* Workout Package */}
          {/* <div className="formInputBox">
            <label htmlFor="workoutPackage" className="formInputLabel">
              Workout Package
            </label>
            <div
              className={`commonWrapper ${
                formErrors.workoutPackage ? "formError" : ""
              }`}
              ref={assignRef("workoutPackage")}>
              <SelectDropdown
                options={workoutPackageOptions}
                selectedOption={formValues.workoutPackage}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "workoutPackage")
                }
                name="Workout Package"
                isLoading={loading}
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">
              {formErrors.workoutPackage || ""}
            </span>
          </div> */}

          {/* Duration */}
          <div className="formInputBox">
            <label htmlFor="duration" className="formInputLabel">
              Duration in Days <span className="required">*</span>
            </label>
            <input
              type="number"
              placeholder="Enter Duration"
              className={`formInput ${formErrors.duration ? "formError" : ""}`}
              name="duration"
              value={formValues.duration}
              onChange={onChangeInput}
              ref={assignRef("duration")}
              disabled={isDisabled}
            />
            <span className="error-label">{formErrors.duration || ""}</span>
          </div>

          {/* Base Price Section */}
          <div className="formInputBox span4">
            <div className="flex-k items-center-k gap-2-k">
              <div className="w-full-k">
                <label className="formInputLabel">
                  Base Price <span className="required">*</span>
                </label>
                <input
                  type="number"
                  placeholder="Enter Base Price"
                  className={`formInput ${
                    formErrors.basePrice ? "formError" : ""
                  }`}
                  name="basePrice"
                  value={formValues.basePrice}
                  onChange={onChangeInput}
                  ref={assignRef("basePrice")}
                  disabled={isDisabled}
                />
                <span className="error-label">
                  {formErrors.basePrice || ""}
                </span>
              </div>
              <span className="fs-2">+</span>
              <div className="w-full-k">
                <label className="formInputLabel">GST Amount</label>
                <input
                  type="text"
                  className="formInput"
                  value={`₹ ${formValues.basePriceGst}`}
                  disabled
                />
              </div>
              <span className="fs-2">=</span>
              <div className="w-full-k">
                <label className="formInputLabel">Final Base Price</label>
                <input
                  type="text"
                  className="formInput"
                  value={`₹ ${formValues.finalBasePrice}`}
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Selling Prices Section */}
          <div className="formInputBox span4">
            {/* First Selling Price Option - Always visible */}
            {formValues.sellingPrices.length > 0 && (
              <div className="selling-price-block mb-3">
                <div className="selling-price-header">
                  <h5 className="selling-price-title">
                    Selling Price Option 1
                  </h5>
                  {formValues.sellingPrices.length > 1 && (
                    <button
                      className="toggle-btn"
                      type="button"
                      aria-expanded={showAdditionalSellingPrices}
                      onClick={() =>
                        setShowAdditionalSellingPrices(
                          !showAdditionalSellingPrices
                        )
                      }>
                      <span>
                        {showAdditionalSellingPrices
                          ? `Hide ${
                              formValues.sellingPrices.length - 1
                            } Additional Options`
                          : `Show ${
                              formValues.sellingPrices.length - 1
                            } Additional Options`}
                      </span>
                    </button>
                  )}
                </div>
                <div className="flex-k items-center-k gap-2-k">
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Final Selling Price <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Final Selling Price"
                      className={`formInput ${
                        formErrors[`sellingPrice_0`] ? "formError" : ""
                      }`}
                      value={formValues.sellingPrices[0].finalSellingPrice}
                      onChange={(e) => onChangeSellingPrice(0, e.target.value)}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors[`sellingPrice_0`] || ""}
                    </span>
                  </div>
                  <span className="fs-2">-</span>
                  <div className="w-full-k">
                    <label className="formInputLabel">GST Amount</label>
                    <input
                      type="text"
                      className="formInput"
                      value={`₹ ${formValues.sellingPrices[0].finalSellingPriceGst}`}
                      disabled
                    />
                  </div>
                  <span className="fs-2">=</span>
                  <div className="w-full-k">
                    <label className="formInputLabel">Selling Price</label>
                    <input
                      type="text"
                      className="formInput"
                      value={`₹ ${formValues.sellingPrices[0].sellingPrice}`}
                      disabled
                    />
                  </div>
                  <div className="w-full-k">
                    <label className="formInputLabel">Extendable Days</label>
                    <input
                      type="number"
                      placeholder="Extendable Days"
                      className="formInput"
                      value={formValues.sellingPrices[0].extendableDays}
                      onChange={(e) =>
                        onChangeExtendableDays(0, e.target.value)
                      }
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Additional Selling Price Options - Collapsible */}
            <div
              className={`collapsible-content ${
                showAdditionalSellingPrices ? "expanded" : ""
              }`}>
              {formValues.sellingPrices.slice(1).map((price, index) => (
                <div key={index} className="selling-price-block">
                  <div className="selling-price-header">
                    <h5 className="selling-price-title">
                      Selling Price Option {index + 2}
                    </h5>
                  </div>
                  {!isDisabled && (
                    <button
                      className="remove-btn"
                      onClick={() => removeSellingPrice(index + 1)}>
                      X
                    </button>
                  )}
                  <div className="flex-k items-center-k gap-2-k">
                    <div className="w-full-k">
                      <label className="formInputLabel">
                        Final Selling Price <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="Enter Final Selling Price"
                        className={`formInput ${
                          formErrors[`sellingPrice_${index + 1}`]
                            ? "formError"
                            : ""
                        }`}
                        value={price.finalSellingPrice}
                        onChange={(e) =>
                          onChangeSellingPrice(index + 1, e.target.value)
                        }
                        disabled={isDisabled}
                      />
                      <span className="error-label">
                        {formErrors[`sellingPrice_${index + 1}`] || ""}
                      </span>
                    </div>
                    <span className="fs-2">-</span>
                    <div className="w-full-k">
                      <label className="formInputLabel">GST Amount</label>
                      <input
                        type="text"
                        className="formInput"
                        value={`₹ ${price.finalSellingPriceGst}`}
                        disabled
                      />
                    </div>
                    <span className="fs-2">=</span>
                    <div className="w-full-k">
                      <label className="formInputLabel">Selling Price</label>
                      <input
                        type="text"
                        className="formInput"
                        value={`₹ ${price.sellingPrice}`}
                        disabled
                      />
                    </div>
                    <div className="w-full-k">
                      <label className="formInputLabel">Extendable Days</label>
                      <input
                        type="number"
                        placeholder="Extendable Days"
                        className="formInput"
                        value={price.extendableDays}
                        onChange={(e) =>
                          onChangeExtendableDays(index + 1, e.target.value)
                        }
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-k items-end-k">
              {!isDisabled && (
                <button
                  className="add-selling-price-btn mt-0"
                  type="button"
                  onClick={addSellingPrice}>
                  Add Selling Price
                </button>
              )}
            </div>
          </div>

          {/* Description - Moved to the end of the form */}
          <div
            className={`formInputBox span4 description-section mt-0 pt-0 ${
              isDisabled ? "disabled" : ""
            }`}>
            <label htmlFor="description" className="formInputLabel">
              Description <span className="required">*</span>
            </label>

            <div className="commonWrapper textAreaCommonWrapper with-auto-height no-border">
              <ReactQuillTextEditor
                value={formValues.description}
                setValue={(content) =>
                  setFormValues((prev) => ({ ...prev, description: content }))
                }
                isError={formErrors?.description ? "formError" : ""}
                loading={loading || isDisabled}
                // readOnly={isDisabled}
              />
            </div>

            <span className="error-label">{formErrors.description || ""}</span>
          </div>

          {/* Plan Settings */}
          <div
            className={`formInputBox checkbox-wrapper span4 ${
              isDisabled ? "disabled" : ""
            }`}>
            <div className="flex-box">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isPartialAllowed"
                  checked={formValues.isPartialAllowed}
                  onChange={onChangeInput}
                  disabled={isDisabled}
                />
                Partial Payment
              </label>
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isCouplePlan"
                  checked={formValues.isCouplePlan}
                  onChange={onChangeInput}
                  disabled={isDisabled}
                />
                Couple Plan
              </label>
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isAllGymAccess"
                  checked={formValues.isAllGymAccess}
                  onChange={onChangeInput}
                  disabled={isDisabled}
                />
                All Gym Access
              </label>
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isPlanActive"
                  checked={formValues.isPlanActive}
                  onChange={onChangeInput}
                  disabled={isDisabled}
                />
                Active
              </label>
            </div>
          </div>

          {/* Couple Plan Selling Prices Section - Only show when Couple Plan is checked */}
          {formValues.isCouplePlan && formValues.coupleSellingPrices && (
            <>
              <div className="formInputBox">
                <label htmlFor="couplePlanName" className="formInputLabel">
                  Couple Plan Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Couple Plan Name"
                  className={`formInput ${
                    formErrors.couplePlanName ? "formError" : ""
                  }`}
                  name="couplePlanName"
                  value={formValues.couplePlanName}
                  onChange={onChangeInput}
                  ref={assignRef("couplePlanName")}
                  disabled={isDisabled}
                />
                <span className="error-label">
                  {formErrors.couplePlanName || ""}
                </span>
              </div>

              <div className="formInputBox span4">
                {/* First Couple Selling Price Option - Always visible */}
                {formValues.coupleSellingPrices.length > 0 && (
                  <div className="selling-price-block mb-3">
                    <div className="selling-price-header">
                      <h5 className="selling-price-title">
                        Couple Selling Price Option 1
                      </h5>
                      {formValues.coupleSellingPrices.length > 1 && (
                        <button
                          className="toggle-btn"
                          type="button"
                          aria-expanded={showAdditionalCoupleSellingPrices}
                          onClick={() =>
                            setShowAdditionalCoupleSellingPrices(
                              !showAdditionalCoupleSellingPrices
                            )
                          }>
                          <span>
                            {showAdditionalCoupleSellingPrices
                              ? `Hide ${
                                  formValues.coupleSellingPrices.length - 1
                                } Additional Options`
                              : `Show ${
                                  formValues.coupleSellingPrices.length - 1
                                } Additional Options`}
                          </span>
                        </button>
                      )}
                    </div>
                    <div className="flex-k items-center-k gap-2-k">
                      <div className="w-full-k">
                        <label className="formInputLabel">
                          Final Selling Price{" "}
                          <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          placeholder="Enter Final Selling Price"
                          className={`formInput ${
                            formErrors[`coupleSellingPrice_0`]
                              ? "formError"
                              : ""
                          }`}
                          value={
                            formValues.coupleSellingPrices[0].finalSellingPrice
                          }
                          onChange={(e) =>
                            onChangeCoupleSellingPrice(0, e.target.value)
                          }
                          disabled={isDisabled}
                        />
                        <span className="error-label">
                          {formErrors[`coupleSellingPrice_0`] || ""}
                        </span>
                      </div>
                      <span className="fs-2">-</span>
                      <div className="w-full-k">
                        <label className="formInputLabel">GST Amount</label>
                        <input
                          type="text"
                          className="formInput"
                          value={`₹ ${formValues.coupleSellingPrices[0].finalSellingPriceGst}`}
                          disabled
                        />
                      </div>
                      <span className="fs-2">=</span>
                      <div className="w-full-k">
                        <label className="formInputLabel">Selling Price</label>
                        <input
                          type="text"
                          className="formInput"
                          value={`₹ ${formValues.coupleSellingPrices[0].sellingPrice}`}
                          disabled
                        />
                      </div>
                      <div className="w-full-k">
                        <label className="formInputLabel">
                          Extendable Days
                        </label>
                        <input
                          type="number"
                          placeholder="Extendable Days"
                          className="formInput"
                          value={
                            formValues.coupleSellingPrices[0].extendableDays
                          }
                          onChange={(e) =>
                            onChangeCoupleExtendableDays(0, e.target.value)
                          }
                          disabled={isDisabled}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Couple Selling Price Options - Collapsible */}
                <div
                  className={`collapsible-content ${
                    showAdditionalCoupleSellingPrices ? "expanded" : ""
                  }`}>
                  {formValues.coupleSellingPrices
                    .slice(1)
                    .map((price, index) => (
                      <div key={index} className="selling-price-block">
                        <div className="selling-price-header">
                          <h5 className="selling-price-title">
                            Couple Selling Price Option {index + 2}
                          </h5>
                        </div>
                        {!isDisabled && (
                          <button
                            className="remove-btn"
                            onClick={() => removeCoupleSellingPrice(index + 1)}>
                            X
                          </button>
                        )}
                        <div className="flex-k items-center-k gap-2-k">
                          <div className="w-full-k">
                            <label className="formInputLabel">
                              Final Selling Price{" "}
                              <span className="required">*</span>
                            </label>
                            <input
                              type="number"
                              placeholder="Enter Final Selling Price"
                              className={`formInput ${
                                formErrors[`coupleSellingPrice_${index + 1}`]
                                  ? "formError"
                                  : ""
                              }`}
                              value={price.finalSellingPrice}
                              onChange={(e) =>
                                onChangeCoupleSellingPrice(
                                  index + 1,
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                            />
                            <span className="error-label">
                              {formErrors[`coupleSellingPrice_${index + 1}`] ||
                                ""}
                            </span>
                          </div>
                          <span className="fs-2">-</span>
                          <div className="w-full-k">
                            <label className="formInputLabel">GST Amount</label>
                            <input
                              type="text"
                              className="formInput"
                              value={`₹ ${price.finalSellingPriceGst}`}
                              disabled
                            />
                          </div>
                          <span className="fs-2">=</span>
                          <div className="w-full-k">
                            <label className="formInputLabel">
                              Selling Price
                            </label>
                            <input
                              type="text"
                              className="formInput"
                              value={`₹ ${price.sellingPrice}`}
                              disabled
                            />
                          </div>
                          <div className="w-full-k">
                            <label className="formInputLabel">
                              Extendable Days
                            </label>
                            <input
                              type="number"
                              placeholder="Extendable Days"
                              className="formInput"
                              value={price.extendableDays}
                              onChange={(e) =>
                                onChangeCoupleExtendableDays(
                                  index + 1,
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                <div className="flex-k items-end-k">
                  {!isDisabled && (
                    <button
                      className="add-selling-price-btn mt-0"
                      type="button"
                      onClick={addCoupleSellingPrice}>
                      Add Couple Selling Price
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Partial Payment Settings - Only show when Partial Allowed is checked */}
          {formValues.isPartialAllowed && (
            <div className="formInputBox span4">
              <div className="installment-settings-container">
                <h4 className="installment-section-title">
                  Partial Payment Settings
                </h4>

                {/* First Installment */}
                <div className="flex-k gap-2-k installment-row">
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      First Installment Percent (%)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Percent"
                      className={`formInput ${
                        formErrors.firstInstallmentPercent ? "formError" : ""
                      }`}
                      name="firstInstallmentPercent"
                      value={formValues.firstInstallmentPercent}
                      onChange={onChangeInput}
                      ref={assignRef("firstInstallmentPercent")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.firstInstallmentPercent || ""}
                    </span>
                  </div>
                </div>

                {/* Second Installment */}
                <div className="flex-k gap-2-k installment-row">
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Second Installment Percent (%)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Percent"
                      className={`formInput ${
                        formErrors.secondInstallmentPercent ? "formError" : ""
                      }`}
                      name="secondInstallmentPercent"
                      value={formValues.secondInstallmentPercent}
                      onChange={onChangeInput}
                      ref={assignRef("secondInstallmentPercent")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.secondInstallmentPercent || ""}
                    </span>
                  </div>
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Second Installment Due Days
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Days"
                      className={`formInput ${
                        formErrors.secondInstallmentDueDate ? "formError" : ""
                      }`}
                      name="secondInstallmentDueDate"
                      value={formValues.secondInstallmentDueDate}
                      onChange={onChangeInput}
                      ref={assignRef("secondInstallmentDueDate")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.secondInstallmentDueDate || ""}
                    </span>
                  </div>
                </div>

                {/* Third Installment */}
                <div className="flex-k gap-2-k installment-row">
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Third Installment Percent (%)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Percent"
                      className={`formInput ${
                        formErrors.thirdInstallmentPercent ? "formError" : ""
                      }`}
                      name="thirdInstallmentPercent"
                      value={formValues.thirdInstallmentPercent}
                      onChange={onChangeInput}
                      ref={assignRef("thirdInstallmentPercent")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.thirdInstallmentPercent || ""}
                    </span>
                  </div>
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Third Installment Due Days
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Days"
                      className={`formInput ${
                        formErrors.thirdInstallmentDueDate ? "formError" : ""
                      }`}
                      name="thirdInstallmentDueDate"
                      value={formValues.thirdInstallmentDueDate}
                      onChange={onChangeInput}
                      ref={assignRef("thirdInstallmentDueDate")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.thirdInstallmentDueDate || ""}
                    </span>
                  </div>
                </div>

                {/* Max Partial Payments */}
                <div className="flex-k gap-2-k installment-row">
                  <div className="w-full-k">
                    <label className="formInputLabel">
                      Max Partial Payments Per Month
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Maximum"
                      className={`formInput ${
                        formErrors.maxPartialPaymentsPerMonth ? "formError" : ""
                      }`}
                      name="maxPartialPaymentsPerMonth"
                      value={formValues.maxPartialPaymentsPerMonth}
                      onChange={onChangeInput}
                      ref={assignRef("maxPartialPaymentsPerMonth")}
                      disabled={isDisabled}
                    />
                    <span className="error-label">
                      {formErrors.maxPartialPaymentsPerMonth || ""}
                    </span>
                  </div>
                </div>

                <p className="installment-note">
                  Note: Total of all installment percentages must equal 100%
                </p>
              </div>
            </div>
          )}
        </div>

        {!isDisabled && (
          <CloseSubmitBtn
            closeLabel="Cancel"
            submitLabel={isEdit ? "Update Plan" : "Add Plan"}
            onClose={closeModal}
            onSubmit={onSubmit}
            isLoading={false}
          />
        )}
      </form>
    </div>
  );
};

export default CentralisedForm;
